import { useRouter } from "next/navigation";
import { createBrowserClient } from "../supabase/config";
import { useEffect, useState, useRef } from "react";
import { User } from "@supabase/supabase-js";

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const router = useRouter();
  const supabase = createBrowserClient();

  // Track previous user to avoid spurious refreshes
  const prevUserRef = useRef<User | null>(null);

  // Use Supabase client to manage session
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });
    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Sign in with email/password via Supabase client
  const signIn = async (email: string, password: string) => {
    const {
      data: { session },
      error,
    } = await supabase.auth.signInWithPassword({ email, password });
    if (error || !session) throw new Error(error?.message || "Login failed");
    setUser(session.user);
    router.refresh();
  };

  // Sign up with email/password via Supabase client
  const signUp = async (email: string, password: string) => {
    const {
      data: { user: newUser },
      error,
    } = await supabase.auth.signUp({ email, password });
    if (error) throw new Error(error.message || "Signup failed");
    setUser(newUser);
  };

  // Sign out via Supabase client
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw new Error(error.message || "Logout failed");
    setUser(null);
    router.push("/");
  };

  // Delete user account via Supabase client
  const deleteAccount = async () => {
    try {
      setIsDeleting(true);
      const {
        data: { user },
        error: getUserError,
      } = await supabase.auth.getUser();
      if (getUserError || !user) throw new Error("Not authenticated");
      // Needs admin privileges; consider using service role on server
      console.warn(
        "Client-side deleteUser requires service role; implement on server if needed."
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    user,
    loading,
    isDeleting,
    signIn,
    signUp,
    signOut,
    deleteAccount,
  };
}
